<script lang="ts" setup>
import { type Component, computed, inject, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import actions from "~/actions";
import Button from "~/components/dumb/Button.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import TaskOrDocDropdownItem from "~/components/dumb/TaskOrDocDropdownItem.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import { EXCLUDE_CURRENT_TASK_REASON } from "~/constants/relationship";
import { ParentRelationshipIcon, PlusIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import {
  ButtonSize,
  ButtonStyle,
  EditorMode,
  FolderSort,
  IconSize,
  Placement,
  RelationshipKindKind,
  TaskSourceKind,
  UserRole,
  ViewKind,
} from "~/shared/enums";
import type { Doc, Task } from "~/shared/types";
import { useAppStore, useDataStore, useUserStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { makeDuid } from "~/utils/common";
import { makeTaskComparatorWithDisabledAndCurrentDartboard } from "~/utils/comparator";
import { useAsyncTasks, useAsyncTasksOrdered, usePaginatedTasksWithSearch } from "~/utils/entityIncrementalLoading";
import { getOrdersBetween } from "~/utils/orderManager";
import usePaginatedList from "~/utils/usePaginatedList";
import { validateIsNotEmpty } from "~/utils/validation";

const props = defineProps<{
  task: Task;
  tasks?: Task[];
  relationshipKindKind?: RelationshipKindKind;
  icon: Component;
  title: string;
  isDoc?: boolean;
  isButton?: boolean;
  isForward?: boolean;
  singleselect?: boolean;
  excludeDuids?: Set<string>;
  excludeReason?: string;
  editorMode?: EditorMode;
  onAfterClose?: () => void;
  itemIndex?: number;
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const userStore = useUserStore();

const menu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const scrollContainer = computed(() => menu.value?.scrollContainer ?? null);

const isOpen = computed(() => menu.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const search = ref("");

const pageDuid = computed(() => `task-dropdown-${search.value}`);
const searchedDocs = ref<Doc[]>(
  dataStore.getAllLoadedDocsForUiOnly().filter((e) => e.title.toLowerCase().includes(search.value.toLowerCase()))
);

const {
  searchedTasks,
  isLoading: isLoadingTasks,
  reset: resetTasks,
} = usePaginatedTasksWithSearch(pageDuid, scrollContainer, search);

const { tasks: recentTasks } = useAsyncTasks(
  computed(() => userStore.recentTaskStack),
  computed(() => ({ includeTrashed: false, includeDraft: false }))
);

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);
const isChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);

const relatedDocDuids = computed(() => dataStore.getDocDuidsRelatedToTask(props.task));

const selectedTasks = computed(() =>
  isChipMode.value
    ? []
    : dataStore.getTasksByDuidsIfLoaded(
        props.tasks
          ? props.tasks.map((e) => e.duid)
          : appStore
              .getActiveVisualization()
              .getSelected()
              .map((e) => e.duid),
        { includeTrashed: false, includeDraft: false }
      )
);
const isMultipleTasks = computed(() => selectedTasks.value.length > 1);
const tasksToProcess = computed(() => (isMultipleTasks.value ? selectedTasks.value : [props.task]));

const relationshipKind = computed(() => {
  if (!props.relationshipKindKind || props.isDoc) {
    return null;
  }
  return dataStore.getRelationshipKindByKind(props.relationshipKindKind);
});
const relationshipKindDuid = computed(() => relationshipKind.value?.duid);
const getTaskRelationships = computed(() => (task: Task) => {
  if (!props.relationshipKindKind || props.isDoc) {
    return [];
  }
  return dataStore.getRelationshipsByKindKind(task, props.relationshipKindKind, props.isForward, {
    includeTrashed: isTrash.value,
  });
});
const targetTaskDuids = computed(() => getTaskRelationships.value(props.task).map((e) => e.targetDuid));
const { tasks: targetTasks } = useAsyncTasksOrdered(
  computed(() => targetTaskDuids.value),
  computed(() => ({ includeTrashed: isTrash.value, includeDraft: false }))
);

const onSelectTask = (taskDuid: string) => {
  if (!relationshipKindDuid.value) {
    return;
  }
  tasksToProcess.value.forEach(async (task) => {
    if (props.relationshipKindKind === RelationshipKindKind.PARENT_OF) {
      const targetTask = await dataStore.getTaskByDuid(taskDuid);
      if (!targetTask) {
        return;
      }
      const child = props.isForward ? targetTask : task;
      const parent = props.isForward ? task : targetTask;
      dataStore.changeParent([{ task: child, order: child.order }], parent);
      return;
    }

    const relationships = getTaskRelationships.value(task);
    const firstRelationship = relationships[0];
    if (props.singleselect && firstRelationship) {
      dataStore.deleteRelationship(task.duid, firstRelationship.duid);
    }

    const sourceDuid = props.isForward ? task.duid : taskDuid;
    const targetDuid = props.isForward ? taskDuid : task.duid;
    dataStore.createRelationship(sourceDuid, targetDuid, relationshipKindDuid.value ?? "");
  });
};

const onCreateTask = async (title: string) => {
  const dartboard = appStore.currentDartboardOrDefault;
  if (!relationshipKindDuid.value || !dartboard) {
    return;
  }

  const order = getOrdersBetween(undefined, dartboard.topTaskOrder)[0];

  const taskRelationships = tasksToProcess.value.map((task) => ({
    duid: makeDuid(),
    kindDuid: relationshipKindDuid.value ?? "",
    targetDuid: task.duid,
    isForward: !props.isForward,
  }));

  const sourceTask = tasksToProcess.value[0];
  const { drafterDuid } = sourceTask;

  const newTask = await dataStore.createTask(title, dartboard.duid ?? "", order, TaskSourceKind.APP_RELATIONSHIP, {
    relationships: taskRelationships,
    drafterDuid,
  });
  searchedTasks.value = [newTask, ...searchedTasks.value];

  resetTasks();
};

const removeTask = (taskDuid: string) => {
  tasksToProcess.value.forEach((task) => {
    const relationships = getTaskRelationships.value(task);
    const relationship = relationships.find((e) => e.targetDuid === taskDuid);
    if (!relationship) {
      return;
    }
    dataStore.deleteRelationship(task.duid, relationship.duid);
  });
};

const { state: relations } = asyncState(
  async () => {
    if (props.isForward === undefined || !props.relationshipKindKind) {
      return [];
    }

    const tasks = await Promise.all(
      tasksToProcess.value.map((task) => {
        if (!props.relationshipKindKind) {
          return [];
        }
        return dataStore.getSourcesOrTargetsExtended(task, props.relationshipKindKind, props.isForward);
      })
    );
    return tasks.flat();
  },
  {
    default: props.relationshipKindKind
      ? dataStore.getTasksByDuidsIfLoaded(
          dataStore.getSourceOrTargetDuidsUiOnly(props.task, props.relationshipKindKind, props.isForward, true),
          { includeTrashed: false, includeDraft: false }
        )
      : [],
    watch: [tasksToProcess, () => props.isForward],
  }
);

const extendedRelationSet = computed(() => new Set(relations.value.map((e) => e.duid)));

const getTaskDisabledAndReason = (task: Task) => {
  if (tasksToProcess.value.some((e) => e.duid === task.duid)) {
    return { disabled: true, reason: EXCLUDE_CURRENT_TASK_REASON };
  }
  if (props.excludeDuids?.has(task.duid)) {
    return { disabled: true, reason: props.excludeReason };
  }
  if (props.relationshipKindKind === RelationshipKindKind.PARENT_OF && props.isForward) {
    if (targetTaskDuids.value.includes(task.duid)) {
      return { disabled: true, reason: "This task is already a subtask of the current task" };
    }
  }
  if (extendedRelationSet.value.has(task.duid) && relationshipKind.value) {
    return {
      disabled: true,
      reason: `This task already ${props.isForward ? relationshipKind.value.forwardText : relationshipKind.value.backwardText} the current task at some level`,
    };
  }
  return { disabled: false, reason: undefined };
};

const comparator = makeTaskComparatorWithDisabledAndCurrentDartboard(
  dataStore.getSpaceByDuid,
  dataStore.getDartboardByDuid,
  (task) => getTaskDisabledAndReason(task).disabled,
  appStore.currentDartboardOrDefault
);

const createTaskItem = (task: Task, customKey?: string) => {
  const { disabled, reason } = getTaskDisabledAndReason(task);
  const selected = targetTasks.value.some((e) => e.duid === task.duid);
  return {
    value: task.duid,
    label: task.title,
    selected,
    disabled,
    disabledReason: reason,
    customKey,
    component: TaskOrDocDropdownItem,
    componentArgs: {
      task,
      label: task.title,
      selected,
      disabled,
      lineThrough: dataStore.defaultCompletedStatusDuids.has(task.statusDuid),
    },
  };
};

const taskSections = computed(() => {
  const sortedEntries = actions.task.groupAndSortTasksByDartboard(searchedTasks.value, props.task.dartboardDuid);

  const recentSection =
    userStore.recentTaskStack.length > 0 && recentTasks.value.length > 0
      ? [
          {
            title: "Recent",
            showTitle: true,
            items: recentTasks.value.map((task) => createTaskItem(task, `recent/${task.duid}`)),
          },
        ]
      : [];

  const regularSections = sortedEntries.map(([title, tasks]) => ({
    title,
    showTitle: true,
    items: tasks.sort(comparator).map((task) => createTaskItem(task)),
  }));

  return [...recentSection, ...regularSections];
});

const createDocItem = (doc: Doc, customKey?: string) => {
  const label = doc.title;
  const selected = relatedDocDuids.value.some((e) => e === doc.duid);
  const disabled = false;

  return {
    value: doc.duid,
    label,
    selected,
    disabled,
    customKey,
    component: TaskOrDocDropdownItem,
    componentArgs: {
      doc,
      label: doc.title,
      selected: relatedDocDuids.value.some((e) => e === doc.duid),
      disabled: false,
      lineThrough: false,
      showOpen: false,
      hideClose: false,
      onRemove: () => {
        dataStore.deleteTaskDocRelationship(props.task.duid, doc.duid);
      },
    },
  };
};

const docSections = computed(() => {
  const recentSection =
    userStore.recentDocStack.length > 0
      ? [
          {
            title: "Recent",
            showTitle: true,
            items: userStore.recentDocStack
              .map((duid) => dataStore.getDocByDuidIfLoaded(duid))
              .filter(isDefined)
              .map((doc) => createDocItem(doc, `recent/${doc.duid}`)),
          },
        ]
      : [];

  // we don't have a current doc folder to prioritize in this context
  const sortedEntries = actions.doc.groupAndSortDocsByFolder(searchedDocs.value);

  const regularSections = sortedEntries.map(([title, groupedDocs]) => ({
    title,
    showTitle: true,
    items: groupedDocs.map((doc) => createDocItem(doc)),
  }));

  return [...recentSection, ...regularSections];
});

const onSelectDoc = async (docDuid: string) => {
  const doc = await dataStore.getDocByDuid(docDuid);
  if (!doc) {
    return;
  }

  dataStore.createTaskDocRelationship(props.task.duid, doc.duid);
};

const { isLoading: isLoadingDocs, reset: resetDocs } = usePaginatedList(
  computed(() => `recent-docs-${search.value}`),
  scrollContainer,
  computed(() => searchedDocs.value.length),
  async (loadedDocsCount) => {
    const { data } = await dataStore.loadDocs(
      { search: search.value, order: FolderSort.RECENT_DESC },
      loadedDocsCount,
      { key: "recent-docs" }
    );
    searchedDocs.value = Array.from(new Set([...searchedDocs.value, ...data.results]));
    return data.count;
  }
);

const onCreateDoc = async (title: string) => {
  const folder = dataStore.defaultFolder;
  const order = getOrdersBetween(undefined, folder.topDocOrder)[0];

  const newDoc = dataStore.createDoc(title, folder.duid, order, {}, { relatedTaskDuids: [props.task.duid] });
  searchedDocs.value = [await newDoc, ...searchedDocs.value];

  resetDocs();
};

const removeDoc = (doc: Doc) => dataStore.deleteTaskDocRelationship(props.task.duid, doc.duid);

const onSearchTaskOrDoc = (value: string) => {
  search.value = value;
  if (props.isDoc) {
    searchedDocs.value = [];
    resetDocs();
  } else {
    searchedTasks.value = [];
    resetTasks();
  }
};

const onClick = () => {
  menu.value?.open();
};

const onSelectTaskOrDoc = (duid: string) => {
  if (props.isDoc) {
    onSelectDoc(duid);
  } else {
    onSelectTask(duid);
  }
};

const removeTaskOrDoc = (duid: string) => {
  if (props.isDoc) {
    const doc = searchedDocs.value.find((e) => e.duid === duid);
    if (doc) {
      removeDoc(doc);
    }
  } else {
    removeTask(duid);
  }
};

const onCreateTaskOrDoc = async (title: string) => {
  if (props.isDoc) {
    await onCreateDoc(title);
  } else {
    await onCreateTask(title);
  }
};

defineExpose({
  open: () => menu.value?.open(),
});
</script>

<template>
  <MultiselectDropdownMenu
    ref="menu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : undefined"
    :sections="isDoc ? docSections : taskSections"
    :header-title="isDoc ? 'Link docs' : 'Link tasks'"
    :placeholder="isDoc ? 'Link a doc...' : 'Link a task...'"
    :placement="Placement.RIGHT_TOP"
    enable-new-entry
    :new-entry-role-override="UserRole.GUEST"
    :validate="validateIsNotEmpty"
    :block="isTaskDetailMode"
    :disabled="isDoc ? task.inTrash : false"
    :width-pixels="448"
    infinite-list
    :loading-more="isDoc ? isLoadingDocs : isLoadingTasks"
    :close-on-select="isDoc ? false : Boolean(singleselect)"
    :on-after-close="onAfterClose"
    @search="onSearchTaskOrDoc"
    @add="onSelectTaskOrDoc"
    @remove="removeTaskOrDoc"
    @create="onCreateTaskOrDoc">
    <DropdownMenuItemContent v-if="!isChipMode && !isButton" :icon="icon" :title="title" is-submenu />
    <Tooltip v-if="isChipMode && !isDoc" text="Change parent" class="w-full">
      <div
        class="flex min-h-[20px] cursor-pointer items-center justify-center gap-1 rounded-sm border border-oncolor px-1 py-0.5 text-md hover:bg-opposite/10">
        <ParentRelationshipIcon
          class="shrink-0"
          :class="[
            isChipMode ? 'icon-sm' : 'icon-xs',
            isChipMode && targetTasks.length !== 0 && 'text-primary-base',
          ]" />
        <span class="select-none">Parent</span>
      </div>
    </Tooltip>
    <Button
      v-if="isButton && isDoc"
      :btn-style="ButtonStyle.SECONDARY"
      :size="ButtonSize.SMALL"
      :icon="PlusIcon"
      :icon-size="IconSize.S"
      a11y-label="Create"
      class="h-6 border-0 !p-1 hover:bg-md print:hidden"
      @click="onClick" />
  </MultiselectDropdownMenu>
</template>
